// WorkbenchGlassBox — derives the universal <GlassBoxTrace> primitive from
// a composer draft's paired AgentTrace row, and renders the version-stamp
// + timing extras beneath it. Mirrors `FindingGlassBox` on the detector
// side; GlassBoxTrace stays universal — no per-composer extras inside it.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Fingerprint, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import type {
    BaseDraft,
    GlassBoxTrace as GlassBoxTraceType,
    TypedCitation,
    CitationKind,
} from '@/data/types';
import GlassBoxTrace from '../GlassBoxTrace';

interface AgentTraceRow {
    id: string;
    artefactId: string;
    artefactType: string;
    citations: Array<{ sourceType: string; sourceId: string; span?: string }>;
    alternativeConclusions: string[];
    counterfactualAnchor: string;
    /** Integer percentage 0-100; null when the agent did not state one. */
    confidence: number | null;
    confidenceGrounding: string;
    modelVersion: string;
    promptVersion: string;
    rulesetVersion: string;
    dataSnapshotTime: string;
    startedAt?: string;
    completedAt?: string;
}

function sourceTypeToKind(sourceType: string): CitationKind {
    const t = sourceType.toLowerCase();
    if (t.includes('handbook') || t.includes('fca')) return 'fca_handbook';
    if (t.includes('fund')) return 'fund_policy';
    if (t.includes('policy') || t.includes('mandate')) return 'firm_policy';
    if (t.includes('kb') || t.includes('note') || t.includes('fact')) return 'kb_note';
    if (t.includes('transcript') || t.includes('call')) return 'transcript';
    if (t.includes('market') || t.includes('price') || t.includes('snapshot')) return 'market_data';
    return 'audit_log';
}

interface WorkbenchGlassBoxProps {
    draft: BaseDraft;
}

export default function WorkbenchGlassBox({ draft }: WorkbenchGlassBoxProps) {
    const { data } = useQuery<AgentTraceRow[]>({
        queryKey: ['agent', 'traces', draft.id],
        queryFn: async () => {
            const res = await api.agent.listAgentTraces({
                query: { artefactId: draft.id },
            });
            if (res.status !== 200) throw new Error(`fetch ${res.status}`);
            return res.body.traces as unknown as AgentTraceRow[];
        },
    });
    const traceRows = data ?? [];
    const traceRow = useMemo(
        () => traceRows.find((t) => t.artefactId === draft.id) ?? null,
        [traceRows, draft.id],
    );

    // Nothing to show when there is no trace row and no draft-level anchor.
    if (!traceRow && !draft.counterfactualAnchor) return null;

    const trace: GlassBoxTraceType = {
        confidenceRationale: traceRow?.confidenceGrounding ?? '',
        confidenceScore: traceRow?.confidence ?? undefined,
        citedEvidence: (traceRow?.citations ?? []).map<TypedCitation>((c) => ({
            kind: sourceTypeToKind(c.sourceType),
            ref: c.sourceId,
            label: `${c.sourceType}${c.span ? ` ${c.span}` : ''}`,
            href: undefined,
        })),
        alternatives: (traceRow?.alternativeConclusions ?? []).map((alt) => ({
            option: alt,
            whyNotChosen: 'See composition plan for the chosen approach.',
        })),
        counterfactuals: draft.counterfactualAnchor
            ? [
                  {
                      condition: 'Anchor condition',
                      wouldChangeTo: draft.counterfactualAnchor,
                  },
              ]
            : traceRow?.counterfactualAnchor
              ? [
                    {
                        condition: 'Anchor condition',
                        wouldChangeTo: traceRow.counterfactualAnchor,
                    },
                ]
              : [],
    };

    return (
        <div className="flex flex-col gap-1.5">
            <GlassBoxTrace trace={trace} />
            {traceRow && (
                <section className="rounded-md border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary)/0.6)] px-3 py-2.5 flex flex-col gap-2">
                    <div className="flex items-center gap-1.5 text-card-title text-[rgb(var(--color-text-primary))]">
                        <Fingerprint className="w-3 h-3" strokeWidth={2} />
                        Version stamp
                    </div>
                    <dl
                        data-testid="workbench-version-stamps"
                        className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-annotation font-mono"
                    >
                        <dt className="text-[rgb(var(--color-text-tertiary))]">model</dt>
                        <dd className="text-[rgb(var(--color-text-secondary))]">
                            {traceRow.modelVersion}
                        </dd>
                        <dt className="text-[rgb(var(--color-text-tertiary))]">prompt</dt>
                        <dd className="text-[rgb(var(--color-text-secondary))]">
                            {traceRow.promptVersion}
                        </dd>
                        <dt className="text-[rgb(var(--color-text-tertiary))]">ruleset</dt>
                        <dd className="text-[rgb(var(--color-text-secondary))]">
                            {traceRow.rulesetVersion}
                        </dd>
                        <dt className="text-[rgb(var(--color-text-tertiary))]">dataSnapshotTime</dt>
                        <dd className="text-[rgb(var(--color-text-secondary))]">
                            {traceRow.dataSnapshotTime}
                        </dd>
                    </dl>
                    {(traceRow.startedAt || traceRow.completedAt) && (
                        <div className="flex items-center gap-1.5 text-annotation text-[rgb(var(--color-text-tertiary))] font-mono pt-1.5 border-t border-[rgb(var(--color-divider)/0.6)]">
                            <Clock className="w-3 h-3" strokeWidth={2} />
                            {traceRow.startedAt ?? '—'} → {traceRow.completedAt ?? '—'}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
